import { SortOption, YesObject } from '@yes.technology/react-toolkit'
import useObjectclass from 'objectclass/shared/hooks/useObjectclass'
import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { updateDisplayText } from 'shared/utils/fields/fields'
import DataTable, {
  DataTableProps
} from 'site/Renderer/components/DataTables/DataTable'
import useChangedFieldValues from 'site/Renderer/components/DataTables/hooks/useChangedFieldValues'
import useTableLayouts, {
  TableLayout
} from 'site/Renderer/components/DataTables/hooks/useTableLayouts'
import useTableMode from 'site/Renderer/components/DataTables/hooks/useTableMode'
import {
  ExtraSelectionAction,
  SelectionAction
} from 'site/Renderer/components/DataTables/types'
import { SelectAndLinkValue } from 'site/Renderer/components/SelectAndLink/SelectAndLink.types'

type useUpdateSortingProps = {
  tableLayout: TableLayout
  setSortOptions: (options: SortOption[]) => void
}

const useUpdateSorting = ({
  tableLayout,
  setSortOptions
}: useUpdateSortingProps) => {
  const orderBy = tableLayout.defaultOrderBy.map(
    ([key, value]) => ({ [key]: value }) as SortOption
  )

  const prevOrderBy = useRef({})

  useEffect(() => {
    if (JSON.stringify(prevOrderBy.current) !== JSON.stringify(orderBy)) {
      setSortOptions(orderBy)
      prevOrderBy.current = orderBy
    }
  }, [orderBy, setSortOptions])
}

type RelationClassTableProps = {
  objectclassUuid: string
  tableObjects: YesObject[]
  currentField: string
  tableId: string
  title: string
  isFetching: boolean
  unsavedObjects: YesObject[]
  setSortOptions: (options: SortOption[]) => void
  setPage: (page: number) => void
  onSave: () => void
  onUnlink: (objects: SelectAndLinkValue[]) => void
} & Pick<
  DataTableProps,
  | 'informational'
  | 'fieldSettings'
  | 'actionButton'
  | 'hierarchyLevel'
  | 'layoutClassificationUuid'
  | 'hideInsertAction'
  | 'selectAndLinkOptions'
  | 'selectAndLinkTargetPath'
  | 'pagination'
  | 'totalObjects'
  | 'sortOptions'
  | 'refetchObjects'
  | 'relationCounterpartField'
  | 'navigationSliderBackgroundColor'
  | 'navigationSliderColor'
  | 'hideSelectAndLinkAction'
  | 'hideDocumentUploadAction'
  | 'uploadAcceptedFileTypes'
>

export const RelationClassTable = ({
  informational,
  objectclassUuid,
  tableObjects,
  fieldSettings,
  tableId,
  title,
  isFetching,
  layoutClassificationUuid,
  hierarchyLevel,
  actionButton,
  hideInsertAction,
  hideSelectAndLinkAction,
  hideDocumentUploadAction,
  uploadAcceptedFileTypes,
  selectAndLinkTargetPath,
  selectAndLinkOptions,
  pagination,
  totalObjects,
  relationCounterpartField,
  navigationSliderColor,
  navigationSliderBackgroundColor,
  unsavedObjects,
  sortOptions,
  setSortOptions,
  setPage,
  refetchObjects,
  onSave,
  onUnlink,
  currentField
}: RelationClassTableProps) => {
  const {
    tableLayouts: [tableLayout]
  } = useTableLayouts({ layoutClassificationUuid })

  useUpdateSorting({
    tableLayout,
    setSortOptions
  })

  const { objectclass } = useObjectclass({ objectclassUuid })

  const { fields = {} } = objectclass || {}

  const updatedFields = useMemo(
    () => updateDisplayText(fields, fieldSettings),
    [fieldSettings, fields]
  )

  const columns = useMemo(() => Object.keys(updatedFields), [updatedFields])

  const unsavedObjectsUuids = useMemo(
    () => unsavedObjects.map((obj) => obj.uuid),
    [unsavedObjects]
  )

  const { t } = useTranslation()

  const saveSelectionAction: SelectionAction = {
    children: t('data-tables.save'),
    onClick: onSave,
    impactClass: 'situationChange',
    pulseAnimate: 'default'
  }

  const primarySelectionAction = unsavedObjects.length
    ? saveSelectionAction
    : undefined

  const extraSelectionActions: ExtraSelectionAction[] = hideSelectAndLinkAction
    ? []
    : [
        {
          children: t('data-tables.unlink'),
          onClick: onUnlink,
          impactClass: 'situationChange'
        }
      ]

  return (
    <DataTable
      informational={informational}
      embedded={true}
      idObjectClass={objectclassUuid}
      objects={tableObjects}
      onSort={setSortOptions}
      setCurrentPage={setPage}
      actionColumnsPosition='last'
      title={title}
      displayLayoutSelection={false}
      hideEditAction
      hasTitleFading
      extendMobileAreaLine
      disableLinkButton
      insertOnly={tableObjects.length === 0}
      highlightedRows={unsavedObjectsUuids}
      relationColumnName={currentField}
      {...{
        objectclass,
        columns,
        fields: updatedFields,
        isLoading: isFetching,
        pagination,
        totalObjects,
        sortOptions,
        actionButton,
        hierarchyLevel,
        refetchObjects,
        relationCounterpartField,
        navigationSliderBackgroundColor,
        navigationSliderColor,
        fieldSettings,
        selectAndLinkOptions,
        primarySelectionAction,
        extraSelectionActions,
        tableLayout,
        hideInsertAction,
        hideSelectAndLinkAction,
        hideDocumentUploadAction,
        selectAndLinkTargetPath,
        id: tableId,
        uploadAcceptedFileTypes
      }}
      {...useTableMode()}
      {...useChangedFieldValues()}
    />
  )
}

export default RelationClassTable
